import SettlementsIcon from "@mui/icons-material/Handshake";
import {
  Box,
  Button,
  ButtonBase,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { lighten } from "@mui/material/styles";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../../context/CurrencyContext";
import { useTheme } from "../../context/ThemeContext";
import { percentage } from "../../shared/percentage";
import {
  signedBar,
  signedTone,
  TABULAR_NUMERALS,
  toneColors,
} from "../general/dataAtoms/shared";
import { COLUMN_HEADING } from "../general/tableStyles";
import { Stage } from "../../shared/CaseConfiguration";
import { StageOfSettlement } from "./CaseOutcomeUtility";

/** The outcomes of a party that leave it with the same net result. */
export type OutcomeGroup = {
  netResult: number;
  // of any outcome of the group
  probability: number;
  // how many outcomes the group has
  outcomes: number;
  // the one group of settlements: its stages, shown when it is opened
  settlementStages?: StageOfSettlement[];
};

/** A row of the table, which opens what its button does. */
const ROW = { cursor: "pointer" } as const;

/** The height of the chart, in px. */
const CHART_HEIGHT = 230;

/**
 * Up to this many bars each carry their net result and their probability.
 * More of them stand too close for that: the table under the chart has both.
 */
const MAX_LABELLED_BARS = 8;

/** The room beside the bars for the labels of the scale, in theme spacing. */
const SCALE_GUTTER = 5.5;

/** No bar is lower than this, so a net result near zero still shows, in px. */
const MIN_BAR_HEIGHT = 3;

/** How far the bar of a probability is lightened from the primary colour. */
const PROBABILITY_BAR_LIGHTEN = 0.2;

/**
 * The groups of a party's outcomes: as bars around zero, lowest net result
 * first, and as the rows of a table under them. A bar and a row open the
 * outcomes of their group.
 */
export const OutcomesByNetResult = ({
  groups,
  onOpen,
  extent = [],
  litigationChanceOf,
  onOpenStage,
}: {
  // sorted by net result, lowest first
  groups: OutcomeGroup[];
  // called with the index of the group to show the outcomes of
  onOpen: (index: number) => void;
  // net results the scale spans too: those of the other view, so that
  // switching between the views leaves the bars their heights
  extent?: number[];
  // in the settlement-adjusted view, how likely a net result is in litigation
  litigationChanceOf?: (netResult: number) => number;
  // called with a stage of the settlements to show the outcomes of
  onOpenStage?: (stage: Stage) => void;
}) => {
  const { theme } = useTheme();
  const { currency } = useCurrency();
  const { t } = useTranslation(undefined, { keyPrefix: "case.analytics" });
  const { t: tShown } = useTranslation(undefined, {
    keyPrefix: "case.shownResults",
  });
  const tones = toneColors(theme);
  const stageName = (stage: Stage) =>
    t(Stage[stage], { keyPrefix: "case.stages" });
  const [stagesOpen, setStagesOpen] = useState(false);
  // settlements open in the table; any other group opens its outcomes
  const open = (index: number) =>
    groups[index].settlementStages ? setStagesOpen(!stagesOpen) : onOpen(index);

  const netResults = [...groups.map(({ netResult }) => netResult), ...extent];
  const lowest = Math.min(0, ...netResults);
  const highest = Math.max(0, ...netResults);
  const range = highest - lowest || 1;
  // where zero is on the scale, in % of its height from the top
  const zeroTop = (highest / range) * 100;
  const isLabelled = groups.length <= MAX_LABELLED_BARS;
  const highestProbability = Math.max(
    ...groups.map(({ probability }) => probability),
  );

  const scaleLabel = {
    position: "absolute",
    left: 0,
    typography: "caption",
    color: "text.secondary",
    ...TABULAR_NUMERALS,
  } as const;

  return (
    <>
      <Box sx={{ px: 2.5, pt: 4.5, pb: isLabelled ? 1 : 3.5 }}>
        <Box sx={{ position: "relative", height: CHART_HEIGHT }}>
          <Box
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              top: `${zeroTop}%`,
              borderTop: `1.5px solid ${theme.palette.text.disabled}`,
            }}
          />
          <Box
            component="span"
            sx={{
              ...scaleLabel,
              top: `${zeroTop}%`,
              transform: "translateY(-110%)",
            }}
          >
            {currency.formatCompact(0)}
          </Box>
          {/* bars without a figure of their own are read off the ends of the scale */}
          {!isLabelled && highest > 0 && (
            <Box
              component="span"
              sx={{ ...scaleLabel, top: 0, transform: "translateY(-110%)" }}
            >
              {currency.formatCompact(highest)}
            </Box>
          )}
          {!isLabelled && lowest < 0 && (
            <Box
              component="span"
              sx={{ ...scaleLabel, bottom: 0, transform: "translateY(110%)" }}
            >
              {currency.formatCompact(lowest)}
            </Box>
          )}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              px: SCALE_GUTTER,
            }}
          >
            {groups.map(
              ({ netResult, probability, settlementStages }, index) => {
                const isLoss = netResult < 0;
                const height = (Math.abs(netResult) / range) * 100;
                const description = [
                  ...(settlementStages ? [t("outcomes.settlement.row")] : []),
                  t("outcomes.probability", {
                    probability: percentage.format(probability),
                  }),
                  ...(litigationChanceOf && !settlementStages
                    ? [
                        tShown("inLitigation", {
                          chance: percentage.format(
                            litigationChanceOf(netResult),
                          ),
                        }),
                      ]
                    : []),
                  t("outcomes.netResult", {
                    amount: currency.format(netResult),
                  }),
                ].join(" · ");
                return (
                  <ButtonBase
                    key={index}
                    onClick={() => open(index)}
                    title={description}
                    aria-label={description}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      position: "relative",
                      display: "block",
                      height: "100%",
                      borderRadius: 1,
                      "&:hover, &.Mui-focusVisible": {
                        bgcolor: "action.hover",
                      },
                    }}
                  >
                    {/* a gain rises from the zero line, a loss hangs from it */}
                    <Box
                      component="span"
                      sx={{
                        position: "absolute",
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: "56%",
                        maxWidth: 96,
                        height: `${height}%`,
                        minHeight: MIN_BAR_HEIGHT,
                        ...(isLoss
                          ? { top: `${zeroTop}%`, borderRadius: "0 0 4px 4px" }
                          : {
                              bottom: `${100 - zeroTop}%`,
                              borderRadius: "4px 4px 0 0",
                            }),
                        // the settlement, lighter than the outcomes at judgment
                        ...signedBar(theme, netResult, !!settlementStages),
                      }}
                    />
                    {/* without labels, the settlement's sign stands past the far
                        end of its bar */}
                    {!isLabelled && settlementStages && (
                      <SettlementsIcon
                        aria-label={t("outcomes.settlement.row")}
                        sx={{
                          position: "absolute",
                          left: "50%",
                          fontSize: 16,
                          color: "text.secondary",
                          ...(isLoss
                            ? {
                                top: `${zeroTop + height}%`,
                                transform: "translate(-50%, 4px)",
                              }
                            : {
                                bottom: `${100 - zeroTop + height}%`,
                                transform: "translate(-50%, -4px)",
                              }),
                        }}
                      />
                    )}
                    {isLabelled && (
                      <Box
                        component="span"
                        sx={{
                          position: "absolute",
                          left: 0,
                          right: 0,
                          // the figure stands past the far end of its bar
                          ...(isLoss
                            ? {
                                top: `${zeroTop + height}%`,
                                transform: "translateY(4px)",
                              }
                            : {
                                bottom: `${100 - zeroTop + height}%`,
                                transform: "translateY(-4px)",
                              }),
                          textAlign: "center",
                          typography: "caption",
                          fontWeight: 700,
                          color: tones[signedTone(netResult)],
                          ...TABULAR_NUMERALS,
                        }}
                      >
                        {currency.formatCompact(netResult)}
                      </Box>
                    )}
                  </ButtonBase>
                );
              },
            )}
          </Box>
        </Box>
        {isLabelled && (
          <Box sx={{ display: "flex", px: SCALE_GUTTER, mt: 3.5 }}>
            {groups.map(({ probability, settlementStages }, index) => (
              <Typography
                key={index}
                variant="caption"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.5,
                  color: "text.secondary",
                  ...TABULAR_NUMERALS,
                }}
              >
                {settlementStages && (
                  <SettlementsIcon
                    aria-label={t("outcomes.settlement.row")}
                    sx={{ fontSize: "1.25em" }}
                  />
                )}
                {t("outcomes.chart.netResults.probability", {
                  probability: percentage.format(probability),
                })}
              </Typography>
            ))}
          </Box>
        )}
      </Box>

      <TableContainer sx={{ mt: 1.5 }}>
        <Table
          size="small"
          sx={{
            // the first and the last column line up with the card's inset
            "& .MuiTableCell-root:first-of-type": { pl: 2.5 },
            "& .MuiTableCell-root:last-of-type": { pr: 2.5 },
            "& .MuiTableBody-root .MuiTableRow-root:last-of-type .MuiTableCell-root":
              { borderBottom: 0 },
          }}
        >
          <TableHead>
            <TableRow sx={{ bgcolor: "background.default" }}>
              <TableCell
                sx={{ ...COLUMN_HEADING, borderTop: 1, borderColor: "divider" }}
              >
                {t("netResult.label")}
              </TableCell>
              <TableCell
                sx={{ ...COLUMN_HEADING, borderTop: 1, borderColor: "divider" }}
              >
                {t("outcomes.column.probability")}
              </TableCell>
              <TableCell
                sx={{ ...COLUMN_HEADING, borderTop: 1, borderColor: "divider" }}
              >
                {t("outcomes.column.outcomes")}
              </TableCell>
              <TableCell sx={{ borderTop: 1, borderColor: "divider" }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map(
              (
                { netResult, probability, outcomes, settlementStages },
                index,
              ) => (
                <Fragment key={index}>
                  {/* the whole row does what its button does */}
                  <TableRow hover onClick={() => open(index)} sx={ROW}>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          component="span"
                          sx={{
                            flex: "none",
                            width: 9,
                            height: 9,
                            borderRadius: 0.5,
                            ...signedBar(theme, netResult),
                          }}
                        />
                        <Typography
                          component="span"
                          sx={{
                            fontWeight: 700,
                            color: tones[signedTone(netResult)],
                            ...TABULAR_NUMERALS,
                          }}
                        >
                          {currency.format(netResult)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ width: "36%" }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.25,
                        }}
                      >
                        {/* as long as the group is likely, the likeliest filling the track */}
                        <Box
                          sx={{
                            flex: 1,
                            height: 6,
                            borderRadius: 3,
                            overflow: "hidden",
                            bgcolor: "action.hover",
                          }}
                        >
                          <Box
                            sx={{
                              width: `${(probability / (highestProbability || 1)) * 100}%`,
                              height: "100%",
                              borderRadius: 3,
                              bgcolor: lighten(
                                theme.palette.primary.main,
                                PROBABILITY_BAR_LIGHTEN,
                              ),
                            }}
                          />
                        </Box>
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{
                            minWidth: 56,
                            textAlign: "right",
                            fontWeight: 600,
                            ...TABULAR_NUMERALS,
                          }}
                        >
                          {percentage.format(probability)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {settlementStages
                          ? t("outcomes.settlement.row")
                          : t("outcomes.judgments.number", {
                              count: outcomes,
                              numberOfJudgments: outcomes,
                            })}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        onClick={(event) => {
                          // the row would act on the same click again
                          event.stopPropagation();
                          open(index);
                        }}
                        aria-expanded={
                          settlementStages ? stagesOpen : undefined
                        }
                        aria-label={t("outcomes.detailsOf", {
                          amount: currency.format(netResult),
                        })}
                        sx={{ textTransform: "none", fontWeight: 600 }}
                      >
                        {settlementStages
                          ? t("outcomes.settlement.stages")
                          : t("outcomes.details")}
                      </Button>
                    </TableCell>
                  </TableRow>
                  {settlementStages &&
                    stagesOpen &&
                    settlementStages.map((row) => (
                      <TableRow
                        key={row.stage}
                        hover={!!onOpenStage}
                        onClick={() => onOpenStage?.(row.stage)}
                        sx={onOpenStage ? ROW : undefined}
                      >
                        <TableCell sx={{ ...TABULAR_NUMERALS, pl: 5 }}>
                          {currency.format(row.netResult)}
                        </TableCell>
                        <TableCell sx={TABULAR_NUMERALS}>
                          {percentage.format(row.probability)}
                        </TableCell>
                        <TableCell>{stageName(row.stage)}</TableCell>
                        <TableCell align="right">
                          {onOpenStage && (
                            <Button
                              size="small"
                              onClick={(event) => {
                                event.stopPropagation();
                                onOpenStage(row.stage);
                              }}
                              aria-label={t("outcomes.settlement.detailsOf", {
                                stage: stageName(row.stage),
                              })}
                              sx={{ textTransform: "none", fontWeight: 600 }}
                            >
                              {t("outcomes.details")}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                </Fragment>
              ),
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};
